import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ChevronUp } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider, useCart } from './context/CartContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import HomePage from './components/pages/HomePage';
import ShopPage from './components/pages/ShopPage';
import PrebookPage from './components/pages/PrebookPage';
import SpecialsPage from './components/pages/SpecialsPage';
import ShopNowPage from './components/pages/ShopNowPage';
import SpecialProductPage from './components/pages/SpecialProductPage';
import ProductPage from './components/pages/ProductPage';
import CartPage from './components/pages/CartPage';
import CheckoutPage from './components/pages/CheckoutPage';
import OrderConfirmation from './components/pages/OrderConfirmation';
import LoginPage from './components/pages/LoginPage';
import RegisterPage from './components/pages/RegisterPage';
import { ForgotPasswordPage, ResetPasswordPage } from './components/pages/PasswordResetPage';
import AccountPage from './components/pages/AccountPage';
import AboutPage from './components/pages/AboutPage';
import ContactPage from './components/pages/ContactPage';
import PoliciesPage from './components/pages/PoliciesPage';
import AdminPanel from './components/pages/AdminPanel';
import './App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function CartLoader() {
  const { user } = useAuth();
  const { fetchCart } = useCart();
  useEffect(() => {
    if (user) fetchCart();
  }, [user, fetchCart]);
  return null;
}

function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  const handleScroll = useCallback(() => {
    setVisible(window.scrollY > 400);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      data-testid="scroll-to-top-button"
      onClick={scrollToTop}
      aria-label="Scroll to top"
      className="fixed bottom-6 right-6 z-50 p-3 rounded-full bg-[#2f3e36] text-white shadow-lg hover:bg-[#253029] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f3e36] focus-visible:ring-offset-2 active:scale-95 transition-all duration-200"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <ChevronUp size={22} strokeWidth={2.5} />
    </button>
  );
}

function NotFoundPage() {
  useEffect(() => {
    const previousTitle = document.title;
    let robots = document.querySelector('meta[name="robots"]');
    const created = !robots;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    const previousContent = robots.getAttribute('content');
    document.title = 'Page not found | Petals West';
    robots.setAttribute('content', 'noindex, nofollow');
    return () => {
      document.title = previousTitle;
      if (created) robots.remove();
      else if (previousContent === null) robots.removeAttribute('content');
      else robots.setAttribute('content', previousContent);
    };
  }, []);
  return (
    <section className="pw-container py-20 text-center" aria-labelledby="not-found-title">
      <p className="text-sm uppercase tracking-widest text-[#6f6a63]">404</p>
      <h1 id="not-found-title" className="mt-3 text-3xl font-semibold">Page not found</h1>
      <p className="mt-4 text-[#6f6a63]">This page may have moved or the address may be incorrect.</p>
      <Link to="/shop" className="mt-7 inline-flex min-h-11 items-center rounded-lg bg-[#2f6b4f] px-6 py-3 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2f6b4f]">Browse flowers</Link>
    </section>
  );
}

function AppContent() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isHome = location.pathname === '/';

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded focus:bg-white focus:px-4 focus:py-3 focus:text-[#121212] focus:shadow-lg">
        Skip to main content
      </a>
      {!isAdmin && <Header />}
      <main id="main-content" tabIndex={-1} className={`flex-1 ${!isAdmin && !isHome ? 'pt-8' : ''}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/prebook" element={<PrebookPage />} />
          <Route path="/specials" element={<SpecialsPage />} />
          <Route path="/specials/:id" element={<SpecialProductPage />} />
          <Route path="/shopnow" element={<ShopNowPage />} />
          <Route path="/product/:slug" element={<ProductPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/account/orders" element={<AccountPage />} />
          <Route path="/account/wishlist" element={<AccountPage />} />
          <Route path="/account/settings" element={<AccountPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/policies/:slug" element={<PoliciesPage />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/admin/products" element={<AdminPanel />} />
          <Route path="/admin/categories" element={<AdminPanel />} />
          <Route path="/admin/orders" element={<AdminPanel />} />
          <Route path="/admin/customers" element={<AdminPanel />} />
          <Route path="/admin/roles" element={<AdminPanel />} />
          <Route path="/admin/audit" element={<AdminPanel />} />
          <Route path="/admin/discounts" element={<AdminPanel />} />
          <Route path="/admin/specials" element={<AdminPanel />} />
          <Route path="/admin/prebook" element={<AdminPanel />} />
          <Route path="/admin/holidays" element={<AdminPanel />} />
          <Route path="/admin/subscribers" element={<AdminPanel />} />
          <Route path="/admin/site-editor" element={<AdminPanel />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      <ScrollToTopButton />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <CartLoader />
          <AppContent />
          <Toaster 
            position="top-right" 
            richColors
            toastOptions={{
              style: {
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '13px',
              }
            }}
          />
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
